import { z } from "zod";
import { IdempotencyKeySchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Whether a clinic may text or email a patient (PA-8 / EXECDEV-215): the decisions that answer
 * it and the one state staff see per channel. A decision is made by the patient online (the
 * booking review or a questionnaire link, PA-11's scheduling.patient_consents) or recorded by
 * staff when the patient says so at the front desk or on the phone (owner decision O5).
 *
 * Scope (owner decision O1, lead decision K4): a decision covers every visit of the patient at
 * this clinic once its booking is linked to their chart; before linking it covers that booking.
 * A decision staff record on the chart covers the chart. The latest decision wins, channel by
 * channel. A STOP reply from the number a text would go to, or a chart that is inactive or
 * deceased, stops messages whatever the decisions say. A newer messaging notice version does not
 * undo an earlier acceptance (PA-11's rule; re-consent on an expanded notice is for counsel).
 *
 * The patient's pre-ticked messaging line is never shown once staff or the chart hold a decision
 * (lead decision K1), so a "Patient said no" cannot be undone by a box the patient did not untick.
 *
 * This module imports nothing at runtime but zod and primitives: patient-messaging.ts,
 * patient-consent.ts and scheduling.ts all import it.
 */
/** The messaging channels, in the order every read lists them (PatientMessageChannelSchema). */
export const PATIENT_MESSAGING_CONSENT_CHANNELS = [
    "sms",
    "email",
];
const MessageChannelSchema = z.enum(PATIENT_MESSAGING_CONSENT_CHANNELS);
/** A consent document version, as patient-consent.ts numbers them. */
const NoticeVersionSchema = z.number().int().positive().max(2_147_483_647);
/** A staff member as reads name them: their display name, or "A staff member"; never an email. */
const StaffNameSchema = z.string().trim().min(1).max(200);
/**
 * How staff heard the patient's decision (the owner's wording): at the front desk, or on the
 * phone. Follow-up bookings made in the room count as front_desk. The staff member who records
 * it is the signed-in member, never a field of the request.
 */
export const StaffMessagingDecisionChannelSchema = z.enum(["front_desk", "phone"]);
/**
 * Where a decision came from: the patient online (PatientConsentChannelSchema: online_booking,
 * intake_link) or staff (StaffMessagingDecisionChannelSchema).
 */
export const PATIENT_MESSAGING_DECISION_SOURCES = [
    "online_booking",
    "intake_link",
    "front_desk",
    "phone",
];
export const PatientMessagingDecisionSourceSchema = z.enum(PATIENT_MESSAGING_DECISION_SOURCES);
/** Whether a source is a staff recording (a person named who recorded it). */
export function isStaffMessagingDecisionSource(source) {
    return source === "front_desk" || source === "phone";
}
/** accepted ("Patient agreed") or declined ("Patient said no", also withdrawing an agreement). */
export const PatientMessagingDecisionValueSchema = z.enum(["accepted", "declined"]);
/**
 * The channels a staff decision covers: texts, emails or both, each once, in the reads' order. A
 * patient's online decision always covers both (the line is one checkbox).
 */
export const PatientMessagingDecisionChannelsSchema = z
    .array(MessageChannelSchema)
    .min(1, "Choose texts, emails or both")
    .max(PATIENT_MESSAGING_CONSENT_CHANNELS.length)
    .refine((channels) => new Set(channels).size === channels.length, "List each channel once")
    .refine((channels) => channels.every((channel, index) => index === 0 ||
    PATIENT_MESSAGING_CONSENT_CHANNELS.indexOf(channel) >
        PATIENT_MESSAGING_CONSENT_CHANNELS.indexOf(channels[index - 1] ?? channel)), "List texts before emails");
// ---------------------------------------------------------------------------
// Staff recording a decision
// ---------------------------------------------------------------------------
const acceptedDecisionShape = {
    decision: z.literal("accepted"),
    channel: StaffMessagingDecisionChannelSchema,
    messageChannels: PatientMessagingDecisionChannelsSchema,
    /**
     * The electronic_messaging notice version staff read to the patient: the clinic's current
     * version. A newer one published since is refused with messaging_notice_changed, so staff read
     * the new text first.
     */
    documentVersion: NoticeVersionSchema,
};
const declinedDecisionShape = {
    decision: z.literal("declined"),
    channel: StaffMessagingDecisionChannelSchema,
    messageChannels: PatientMessagingDecisionChannelsSchema,
    /** The notice version on screen, when staff had it open; a refusal needs none. */
    documentVersion: NoticeVersionSchema.optional(),
};
/**
 * A decision staff record, without its idempotency key: what a staff booking carries as
 * messagingDecision (CreateSchedulingAppointmentRequestSchema), under the booking's own key.
 *
 * - accepted ("Patient agreed"): only while the clinic offers messaging (an offered channel;
 *   messaging_not_offered otherwise) and on its current notice version (messaging_notice_changed
 *   otherwise). A listed channel the clinic does not offer yet is recorded and used once it is.
 * - declined ("Patient said no"): always allowed. It also withdraws an earlier agreement.
 */
export const StaffMessagingDecisionInputSchema = z.discriminatedUnion("decision", [
    z.object(acceptedDecisionShape).strict(),
    z.object(declinedDecisionShape).strict(),
]);
/**
 * "Patient agreed": staff record the patient's spoken agreement to texts or emails (owner
 * decision O5), after reading them the clinic's electronic_messaging notice.
 */
export const RecordPatientMessagingConsentRequestSchema = z
    .object({ ...acceptedDecisionShape, idempotencyKey: IdempotencyKeySchema })
    .strict();
/** "Patient said no": staff record a refusal or the withdrawal of an earlier agreement. */
export const WithdrawPatientMessagingConsentRequestSchema = z
    .object({ ...declinedDecisionShape, idempotencyKey: IdempotencyKeySchema })
    .strict();
/**
 * The body of both staff routes; the target is in the path and the staff member is the signed-in
 * member. Any staff role records (front desk included):
 *
 * - POST /v1/scheduling/appointments/:appointmentId/messaging/decisions: on a booking (a
 *   walk-in's row included). Once the booking is linked to a chart it covers the chart (O1).
 * - POST /v1/patients/:patientId/messaging/decisions: on the chart, at this clinic.
 *
 * The key names one decision: an exact retry returns it (created false); the same key with
 * another body is a plain conflict. Audited without any phone number or address. Responds with
 * RecordPatientMessagingDecisionResponseSchema (patient-messaging-settings.ts).
 */
export const RecordPatientMessagingDecisionRequestSchema = z.discriminatedUnion("decision", [
    RecordPatientMessagingConsentRequestSchema,
    WithdrawPatientMessagingConsentRequestSchema,
]);
// ---------------------------------------------------------------------------
// The state staff see
// ---------------------------------------------------------------------------
/** The decision that settles a channel: the latest in scope. */
export const PatientMessagingDecisionSummarySchema = z
    .object({
    decision: PatientMessagingDecisionValueSchema,
    source: PatientMessagingDecisionSourceSchema,
    decidedAt: IsoDateTimeSchema,
    /** The notice version decided on; null only for a staff refusal recorded without one. */
    documentVersion: NoticeVersionSchema.nullable(),
    /** Who recorded it: staff decisions only. */
    recordedByName: StaffNameSchema.nullable(),
    /**
     * The booking it was made on (always, for a patient's own decision), or null for a decision
     * staff recorded on the chart. A different booking than the one being read means the
     * decision came through the chart (O1).
     */
    appointmentId: z.string().uuid().nullable(),
})
    .strict()
    .superRefine((value, context) => {
    const staff = isStaffMessagingDecisionSource(value.source);
    if (staff !== (value.recordedByName !== null)) {
        context.addIssue({
            code: "custom",
            message: "A staff decision names who recorded it, and only a staff decision",
            path: ["recordedByName"],
        });
    }
    if (!staff && (value.appointmentId === null || value.documentVersion === null)) {
        context.addIssue({
            code: "custom",
            message: "A patient's own decision is made on a booking, on a notice version",
            path: ["appointmentId"],
        });
    }
    if (value.decision === "accepted" && value.documentVersion === null) {
        context.addIssue({
            code: "custom",
            message: "An agreement names the notice version it was given on",
            path: ["documentVersion"],
        });
    }
});
/**
 * One channel's state, most important first:
 *
 * - chart_inactive: the linked chart is inactive or deceased; nothing is sent.
 * - stopped: texts only: the number a text would go to replied STOP to this clinic (PA-7's
 *   ledger). Only the patient lifts it, by replying START; staff cannot. A chart read never says
 *   it: a chart may use several numbers, and the card lists each one's STOP state.
 * - accepted / declined: the latest decision in scope.
 * - not_asked: no decision in scope; nothing is sent.
 *
 * decision is the latest decision in scope whatever the state, so a STOP still shows what the
 * patient had agreed to.
 */
export const PatientMessagingChannelConsentStateSchema = z.enum([
    "accepted",
    "declined",
    "not_asked",
    "stopped",
    "chart_inactive",
]);
export const PatientMessagingChannelConsentSchema = z
    .object({
    state: PatientMessagingChannelConsentStateSchema,
    decision: PatientMessagingDecisionSummarySchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.state === "accepted" || value.state === "declined") &&
        value.decision?.decision !== value.state) {
        context.addIssue({
            code: "custom",
            message: "An accepted or declined channel shows the decision that settled it",
            path: ["decision"],
        });
    }
    if (value.state === "not_asked" && value.decision !== null) {
        context.addIssue({
            code: "custom",
            message: "A channel with a decision has been asked",
            path: ["state"],
        });
    }
});
/**
 * Both channels' states for a booking (its scope under O1) or a chart (at this clinic). Only an
 * accepted channel is ever sent on.
 */
export const PatientMessagingConsentSchema = z
    .object({
    sms: PatientMessagingChannelConsentSchema,
    email: PatientMessagingChannelConsentSchema,
})
    .strict();
/**
 * The channels the clinic offers now (texts, emails), in the reads' order: each one switched on,
 * with a sender set up and a messaging service that delivers (lead decision K3; see
 * patientMessagingChannelReadiness). Empty means messaging is not offered: patients are not
 * asked and staff cannot record an agreement.
 */
export const PatientMessagingOfferedChannelsSchema = z
    .array(MessageChannelSchema)
    .max(PATIENT_MESSAGING_CONSENT_CHANNELS.length)
    .refine((channels) => channels.every((channel, index) => index === 0 ||
    PATIENT_MESSAGING_CONSENT_CHANNELS.indexOf(channel) >
        PATIENT_MESSAGING_CONSENT_CHANNELS.indexOf(channels[index - 1] ?? channel)), "List each channel once, texts before emails");
//# sourceMappingURL=patient-messaging-consent.js.map